import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSync, FaDollarSign, FaChartLine, FaUsers, FaShoppingCart, FaCoins, FaMapMarkerAlt, FaWallet, FaCopy, FaExclamationTriangle } from 'react-icons/fa';
import api from '@/utils/api';
import './page.scss';

interface DashboardStats {
  totalUsers: number;
  activeProfiles: number;
  // Part C of #647 — split of activeProfiles by creator type
  // (creator = business/brand/org/individual, NOT flagged as agent).
  // Sum equals activeProfiles by construction.
  activeCreatorProfiles: number;
  activeAgentProfiles: number;
  totalVolume: number;
  spotlightRevenue: number;
  newUsersLast30Days: number;
}

interface FundingWalletBalance {
  address: string;
  network: string;
  balances: { klv: number | null; klyx: number | null };
  thresholds: { klv: number; klyx: number };
  flags: { klvLow: boolean; klyxLow: boolean };
}

const fmt = (n: number) => n.toLocaleString();
const fmtKlyx = (n: number) => `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KLYX`;
const fmtBalance = (n: number | null, symbol: string) =>
  n === null ? '—' : `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`;
const truncateAddress = (addr: string) =>
  addr.length > 18 ? `${addr.slice(0, 10)}…${addr.slice(-6)}` : addr;

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [dbStatus, setDbStatus] = useState<'loading' | 'operational' | 'error'>('loading');
  const [wallet, setWallet] = useState<FundingWalletBalance | null>(null);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [copiedAddr, setCopiedAddr] = useState(false);

  useEffect(() => {
    api.get('/admin/dashboard')
      .then(({ data }) => {
        setStats(data.stats);
        setDbStatus(data.systemStatus?.database === 'operational' ? 'operational' : 'error');
      })
      .catch(() => setDbStatus('error'));

    // Funding wallet balance is independent of the dashboard payload —
    // a Klever node hiccup shouldn't blank the rest of the stats and
    // vice versa, so it's its own request with its own error state.
    api.get<FundingWalletBalance>('/admin/funding-wallet/balance')
      .then(({ data }) => setWallet(data))
      .catch((err) => setWalletError(err.response?.data?.message ?? err.response?.data?.error ?? 'Failed to load wallet balance'));
  }, []);

  const copyAddress = async () => {
    if (!wallet?.address) return;
    try {
      await navigator.clipboard.writeText(wallet.address);
      setCopiedAddr(true);
      setTimeout(() => setCopiedAddr(false), 1500);
    } catch {
      // navigator.clipboard unavailable in some non-secure contexts;
      // failing silently is fine for an internal admin tool.
    }
  };

  const statCards: Array<{
    label: string;
    value: string;
    icon: typeof FaUsers;
    color: string;
    subline?: string;
  }> = [
    { label: 'Total Users', value: stats ? fmt(stats.totalUsers) : '—', icon: FaUsers, color: 'blue' },
    {
      label: 'Active Profiles',
      value: stats ? fmt(stats.activeProfiles) : '—',
      icon: FaShoppingCart,
      color: 'green',
      // Creator/agent breakdown (Part C of #647). Rendered only when
      // both counts have loaded; sums back to activeProfiles.
      subline: stats ? `${fmt(stats.activeCreatorProfiles)} creator · ${fmt(stats.activeAgentProfiles)} agent` : undefined,
    },
    { label: 'Total Volume', value: stats ? fmtKlyx(stats.totalVolume) : '—', icon: FaCoins, color: 'purple' },
    { label: 'Spotlight Revenue', value: stats ? fmtKlyx(stats.spotlightRevenue) : '—', icon: FaDollarSign, color: 'orange' },
  ];

  const quickActions = [
    {
      title: 'Blockchain Sync',
      description: 'Manage database and blockchain synchronization',
      icon: FaSync,
      color: 'blue',
      path: '/admin/sync',
    },
    {
      title: 'Spotlight Pricing',
      description: 'Configure spotlight pricing tiers',
      icon: FaDollarSign,
      color: 'green',
      path: '/dashboard-admin/spotlight-pricing',
    },
    {
      title: 'Spotlight Analytics',
      description: 'View spotlight performance metrics',
      icon: FaChartLine,
      color: 'purple',
      path: '/dashboard-admin/spotlight-analytics',
    },
    {
      title: 'Location Management',
      description: 'Bulk import or add locations for discovery',
      icon: FaMapMarkerAlt,
      color: 'orange',
      path: '/dashboard-admin/locations',
    },
    {
      title: 'Access Transactions',
      description: 'Reconcile paid-but-not-persisted view + subscribe purchases',
      icon: FaExclamationTriangle,
      color: 'orange',
      path: '/dashboard-admin/access-transactions',
    },
  ];

  return (
    <div className="admin-dashboard-page">
      <div className="admin-dashboard-container">
        <div className="admin-dashboard-header">
          <h1>Admin Dashboard</h1>
          <p className="subtitle">System overview and management</p>
        </div>

        {/* Quick Stats */}
        <div className="stats-grid">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className={`stat-card ${stat.color}`}>
                <div className="stat-icon"><Icon /></div>
                <div className="stat-content">
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                  {stat.subline && <div className="stat-subline">{stat.subline}</div>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Funding wallet — read-only snapshot of the custodial wallet
            that signs welcome bonuses, achievement rewards, and any
            admin-initiated SC calls. Surfaces a low-balance warning so
            we top up before the next batch of signups fails. */}
        {(wallet || walletError) && (
          <div className={`funding-wallet-card${(wallet?.flags.klvLow || wallet?.flags.klyxLow) ? ' is-low' : ''}`}>
            <div className="funding-wallet-card__header">
              <div className="funding-wallet-card__title">
                <FaWallet className="funding-wallet-card__icon" />
                <span>Funding wallet</span>
                <span className="funding-wallet-card__network">{wallet?.network ?? '—'}</span>
              </div>
              {(wallet?.flags.klvLow || wallet?.flags.klyxLow) && (
                <span className="funding-wallet-card__badge">
                  <FaExclamationTriangle /> Low balance
                </span>
              )}
            </div>

            {walletError && (
              <p className="funding-wallet-card__error">{walletError}</p>
            )}

            {wallet && (
              <>
                <button
                  type="button"
                  className="funding-wallet-card__address"
                  onClick={copyAddress}
                  title={wallet.address}
                >
                  <code>{truncateAddress(wallet.address)}</code>
                  <FaCopy /> {copiedAddr ? 'Copied' : 'Copy'}
                </button>

                <div className="funding-wallet-card__balances">
                  <div className={`funding-wallet-card__balance${wallet.flags.klvLow ? ' is-low' : ''}`}>
                    <span className="funding-wallet-card__balance-label">KLV</span>
                    <span className="funding-wallet-card__balance-value">{fmtBalance(wallet.balances.klv, 'KLV')}</span>
                    <span className="funding-wallet-card__balance-threshold">
                      threshold {wallet.thresholds.klv}
                    </span>
                  </div>
                  <div className={`funding-wallet-card__balance${wallet.flags.klyxLow ? ' is-low' : ''}`}>
                    <span className="funding-wallet-card__balance-label">KLYX</span>
                    <span className="funding-wallet-card__balance-value">{fmtBalance(wallet.balances.klyx, 'KLYX')}</span>
                    <span className="funding-wallet-card__balance-threshold">
                      threshold {wallet.thresholds.klyx}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* New users callout */}
        {stats && (
          <div className="admin-callout">
            <span className="callout-label">New users (last 30 days)</span>
            <span className="callout-value">{fmt(stats.newUsersLast30Days)}</span>
          </div>
        )}

        {/* Quick Actions */}
        <div className="admin-section">
          <h2>Quick Actions</h2>
          <div className="actions-grid">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <button
                  key={index}
                  className={`action-card ${action.color}`}
                  onClick={() => navigate(action.path)}
                >
                  <div className="action-icon"><Icon /></div>
                  <div className="action-content">
                    <h3>{action.title}</h3>
                    <p>{action.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* System Status */}
        <div className="admin-section">
          <h2>System Status</h2>
          <div className="status-card">
            <div className="status-row">
              <span className="status-label">Database</span>
              <span className={`status-indicator ${dbStatus === 'loading' ? 'loading' : dbStatus === 'operational' ? 'success' : 'error'}`}>
                <span className="status-dot"></span>
                {dbStatus === 'loading' ? 'Checking…' : dbStatus === 'operational' ? 'Operational' : 'Error'}
              </span>
            </div>
            <div className="status-row">
              <span className="status-label">API Services</span>
              <span className="status-indicator success">
                <span className="status-dot"></span>
                Running
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
